import type { ApiProblemDetails } from "@broadview/contracts";

export type ClientErrorCode =
  | "UNAUTHENTICATED"
  | "FORBIDDEN"
  | "NOT_FOUND"
  | "CONFLICT"
  | "VALIDATION_FAILED"
  | "RATE_LIMITED"
  | "UNAVAILABLE"
  | "CONTRACT_MISMATCH"
  | "LIVE_ADAPTER_DISABLED";

export class BroadviewClientError extends Error {
  readonly status: number;
  readonly code: ClientErrorCode;
  readonly correlationId?: string | undefined;
  readonly retryable?: boolean | undefined;

  constructor(options: {
    message: string;
    status: number;
    code: ClientErrorCode;
    correlationId?: string | undefined;
    retryable?: boolean | undefined;
  }) {
    super(options.message);
    this.name = "BroadviewClientError";
    this.status = options.status;
    this.code = options.code;
    this.correlationId = options.correlationId;
    this.retryable = options.retryable;
  }
}

export function errorFromProblem(problem: ApiProblemDetails): BroadviewClientError {
  const codeByStatus: Record<number, ClientErrorCode> = {
    401: "UNAUTHENTICATED",
    403: "FORBIDDEN",
    404: "NOT_FOUND",
    409: "CONFLICT",
    422: "VALIDATION_FAILED",
    429: "RATE_LIMITED",
  };

  return new BroadviewClientError({
    message: problem.detail ?? problem.title,
    status: problem.status,
    code: codeByStatus[problem.status] ?? "UNAVAILABLE",
    correlationId: problem.correlationId,
    retryable: problem.retryable,
  });
}

export function clientError(
  status: number,
  code: ClientErrorCode,
  message: string,
  metadata: Readonly<{ correlationId?: string | undefined; retryable?: boolean | undefined }> = {},
): BroadviewClientError {
  return new BroadviewClientError({
    status,
    code,
    message,
    correlationId: metadata.correlationId,
    retryable: metadata.retryable,
  });
}
